/**
 * useContagemPageEffects - Side effects and form/auditor/local handlers for ContagemCompletaPage
 *
 * Extracted to keep ContagemCompletaPage under 500 lines.
 * Covers: sync effects, data loading, matching application, beforeunload guard,
 * handleChange, handleSelectHospital, handleAddAuditor, handleRemoveAuditor,
 * handleAddLocal, handleRemoveLocal, handleSaveAll, handleDiscardChanges,
 * formatEnvioSaldos, locaisRetorno state.
 */
import { useState, useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import contagemService from '../services/contagemService';
import jobService from '../services/jobService';
import { msg } from '../utils/messages';
import type { DadoContagem } from './useContagemData';

interface FormData {
  [key: string]: any;
  dataHora: string;
  inicio: string;
  observacoes: string;
  envioSaldosCalculado?: string;
}

interface LocalRetorno {
  id: number;
  nome_local: string;
  lacre?: string | null;
  hora_inicio?: string | null;
  hora_termino?: string | null;
  observacoes?: string | null;
}

interface Auditor {
  id: number;
  nome?: string;
  [key: string]: any;
}

interface LocalContagem {
  id: number;
  nome?: string;
  [key: string]: any;
}

interface UseContagemPageEffectsConfig {
  id: string | undefined;
  isNewContagem: boolean;
  dateParam: string | null;
  formData: FormData;
  setFormData: Dispatch<SetStateAction<FormData>>;
  selectedClienteIds: number[];
  selectedHospitalId: number;
  auditoresParticipantes: Auditor[];
  locaisContagemSelecionados: LocalContagem[];
  isEditMode: boolean;
  originalFormData: any;
  setOriginalFormData: (data: any) => void;
  dadosContagem: DadoContagem[];
  originalDadosContagem: DadoContagem[];
  setDadosContagem: Dispatch<SetStateAction<DadoContagem[]>>;
  setUploadResult: (result: any) => void;
  uploadResult: any;
  modifiedItems: Map<number, Set<string>>;
  modifiedFields: Set<string>;
  setModifiedFields: Dispatch<SetStateAction<Set<string>>>;
  clearModifiedItems: () => void;
  saveOriginalData: () => void;
  setIsEditMode: (mode: boolean) => void;
  setAuditoresParticipantes: Dispatch<SetStateAction<Auditor[]>>;
  setLocaisContagemSelecionados: Dispatch<SetStateAction<LocalContagem[]>>;
  setSelectedHospitalId: Dispatch<SetStateAction<number>>;
  selectedAuditorId: number;
  setSelectedAuditorId: Dispatch<SetStateAction<number>>;
  selectedLocalId: number;
  setSelectedLocalId: Dispatch<SetStateAction<number>>;
  auditoresDisponiveis: Auditor[];
  locaisContagemDisponiveis: LocalContagem[];
  hospitaisDisponiveis: any[];
  loadJobAuditores: (job: any) => Auditor[];
  loadJobLocais: (job: any, hospitalId: number) => Promise<LocalContagem[]>;
  trackFieldModification: (field: string, value: any, original: any) => void;
  trackArrayModification: (field: string, newArr: any[], originalArr: any[]) => void;
  cirurgiaMatching: any;
  conferenciaLacresMatching: any;
  saveHook: any;
}

export function useContagemPageEffects({
  id,
  isNewContagem,
  formData,
  setFormData,
  selectedClienteIds,
  selectedHospitalId,
  auditoresParticipantes,
  locaisContagemSelecionados,
  isEditMode,
  originalFormData,
  setOriginalFormData,
  dadosContagem,
  originalDadosContagem,
  setDadosContagem,
  setUploadResult,
  uploadResult,
  modifiedItems,
  modifiedFields,
  setModifiedFields,
  clearModifiedItems,
  saveOriginalData,
  setIsEditMode,
  setAuditoresParticipantes,
  setLocaisContagemSelecionados,
  setSelectedHospitalId,
  selectedAuditorId,
  setSelectedAuditorId,
  selectedLocalId,
  setSelectedLocalId,
  auditoresDisponiveis,
  locaisContagemDisponiveis,
  hospitaisDisponiveis,
  loadJobAuditores,
  loadJobLocais,
  trackFieldModification,
  trackArrayModification,
  cirurgiaMatching,
  conferenciaLacresMatching,
  saveHook,
}: UseContagemPageEffectsConfig) {

  // Locais Retorno state
  const [locaisRetorno, setLocaisRetorno] = useState<LocalRetorno[]>([]);
  const [loadingLocaisRetorno, setLoadingLocaisRetorno] = useState(false);

  // Load locais retornados
  useEffect(() => {
    if (!id || isNewContagem) return;
    const loadLocaisRetorno = async () => {
      try {
        setLoadingLocaisRetorno(true);
        const data = await jobService.getLocaisRetorno(parseInt(id));
        setLocaisRetorno(data || []);
      } catch (err) {
        console.error('Erro ao carregar locais retorno:', err);
        setLocaisRetorno([]);
      } finally {
        setLoadingLocaisRetorno(false);
      }
    };
    loadLocaisRetorno();
  }, [id, isNewContagem]);

  // Sync job auditores and locais after job loads
  useEffect(() => {
    const syncJobAuditoresAndLocais = async () => {
      if (isNewContagem || !id || !formData.dataHora || selectedHospitalId === 0) return;
      try {
        const job = await jobService.getJob(parseInt(id));
        const jobAuditores = loadJobAuditores(job);
        if (jobAuditores.length > 0) setAuditoresParticipantes(jobAuditores);
        const jobLocais = await loadJobLocais(job, selectedHospitalId);
        if (jobLocais.length > 0) setLocaisContagemSelecionados(jobLocais);
      } catch (error) {
        console.error('Error syncing job auditores/locais:', error);
      }
    };
    syncJobAuditoresAndLocais();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNewContagem, formData.dataHora, selectedHospitalId]);

  // Upload result effect - populate table
  useEffect(() => {
    if (uploadResult && uploadResult.itens) {
      const dadosReais = uploadResult.itens.map((item: any) =>
        contagemService.convertToUIFormat(item)
      );
      setDadosContagem(dadosReais);
    }
  }, [uploadResult, setDadosContagem]);

  // Load dadosContagem for existing job
  useEffect(() => {
    const loadDadosContagem = async () => {
      if (isNewContagem || !id) {
        setDadosContagem([]);
        return;
      }
      try {
        const result = await contagemService.getExcelItemsEpic19(parseInt(id));
        if (result && result.itens) {
          const dadosReais = result.itens.map((item: any) =>
            contagemService.convertToUIFormat(item)
          );
          setDadosContagem(dadosReais);
          setUploadResult(result);
        } else {
          setDadosContagem([]);
        }
      } catch (err: any) {
        console.error('Erro ao carregar dados da contagem:', err);
        setDadosContagem([]);
      }
    };
    loadDadosContagem();
  }, [id, isNewContagem, setDadosContagem, setUploadResult]);

  // Save original data when entering edit mode
  useEffect(() => {
    if (isEditMode && !originalFormData) {
      setOriginalFormData({
        ...formData,
        selectedClienteIds: [...selectedClienteIds],
        selectedHospitalId,
        auditoresParticipantes: [...auditoresParticipantes],
        locaisContagemSelecionados: [...locaisContagemSelecionados],
      });
      saveOriginalData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  // Apply cirurgia and conferencia lacres matching
  useEffect(() => {
    if (!cirurgiaMatching.loading && !conferenciaLacresMatching.loading && dadosContagem.length > 0) {
      const hasCirurgiaData = cirurgiaMatching.matchMap.size > 0;
      const hasLacresData = conferenciaLacresMatching.matchMap.size > 0;
      if (hasCirurgiaData || hasLacresData) {
        const matchedData = dadosContagem.map(item => {
          const cirurgiaMatch = cirurgiaMatching.getMatchForItem({
            produto: item.produto,
            lote: item.lote,
            cnpj_cliente: item.cnpj_cliente,
            regime: item.regime,
          });
          const faturar = cirurgiaMatch?.totalQtd || 0;
          const pacientes = cirurgiaMatch?.pacientes.join(', ') || '';

          const segregado = item.quantidade_segregada ?? 0;
          // coletado já vem mapeado de quantidade_coletada no convertToUIFormat

          const diff = item.contado + faturar - item.sistema;
          const sobras = diff > 0 ? diff : 0;
          const faltas = diff < 0 ? Math.abs(diff) : 0;
          return { ...item, segregado, faturar, pacientes, sobras, faltas };
        });
        setDadosContagem(matchedData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cirurgiaMatching.loading, cirurgiaMatching.matchMap, conferenciaLacresMatching.loading, conferenciaLacresMatching.matchMap, dadosContagem.length, uploadResult]);

  // Warn user before leaving page with unsaved changes
  useEffect(() => {
    const hasUnsavedData = () => {
      if (!isNewContagem) return false;
      return !!(
        formData.dataHora ||
        selectedClienteIds.length > 0 ||
        selectedHospitalId > 0 ||
        auditoresParticipantes.length > 0 ||
        formData.observacoes
      );
    };
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedData()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isNewContagem, formData, selectedClienteIds, selectedHospitalId, auditoresParticipantes]);

  // ==================== HANDLERS ====================

  const handleChange = (field: string, value: string) => {
    setFormData((prev: FormData) => {
      const updated = { ...prev, [field]: value };
      if (field === 'dataHora' || field === 'inicio') {
        try {
          const dataStr = field === 'dataHora' ? value : prev.dataHora;
          const horaStr = field === 'inicio' ? value : prev.inicio;
          if (dataStr && horaStr && dataStr.includes('/') && horaStr.includes(':')) {
            const [day, month, year] = dataStr.split('/').map(Number);
            const [hours, minutes] = horaStr.split(':').map(Number);
            const datetime = new Date(year, month - 1, day, hours, minutes);
            const envioSaldos = new Date(datetime.getTime() - 24 * 60 * 60 * 1000);
            updated.envioSaldosCalculado = envioSaldos.toISOString();
          }
        } catch (error) {
          console.error('Erro ao calcular envio_saldos:', error);
        }
      }
      return updated;
    });
    if (isEditMode && originalFormData) {
      trackFieldModification(field, value, originalFormData[field]);
    }
  };

  const handleSelectHospital = (hospitalId: number) => {
    const hospital = hospitaisDisponiveis.find((h: any) => h.id === hospitalId);
    if (hospital) {
      setSelectedHospitalId(hospitalId);
      if (isEditMode && originalFormData) {
        const originalValue = originalFormData.selectedHospitalId || 0;
        setModifiedFields((prev: Set<string>) => {
          const newSet = new Set(prev);
          if (originalValue !== hospitalId) {
            newSet.add('selectedHospitalId');
          } else {
            newSet.delete('selectedHospitalId');
          }
          return newSet;
        });
      }
    }
  };

  const handleAddAuditor = () => {
    if (!selectedAuditorId) return;
    const auditor = auditoresDisponiveis.find((a: any) => a.id === selectedAuditorId);
    if (auditor && !auditoresParticipantes.some((p: any) => p.id === auditor.id)) {
      const newAuditores = [...auditoresParticipantes, auditor];
      setAuditoresParticipantes(newAuditores);
      setSelectedAuditorId(0);
      trackArrayModification('auditoresParticipantes', newAuditores, originalFormData?.auditoresParticipantes || []);
    }
  };

  const handleRemoveAuditor = () => {
    if (!selectedAuditorId) return;
    const newAuditores = auditoresParticipantes.filter((a: any) => a.id !== selectedAuditorId);
    setAuditoresParticipantes(newAuditores);
    setSelectedAuditorId(0);
    trackArrayModification('auditoresParticipantes', newAuditores, originalFormData?.auditoresParticipantes || []);
  };

  const handleAddLocal = () => {
    if (!selectedLocalId) return;
    const local = locaisContagemDisponiveis.find((l: any) => l.id === selectedLocalId);
    if (local && !locaisContagemSelecionados.some((p: any) => p.id === local.id)) {
      const newLocais = [...locaisContagemSelecionados, local];
      setLocaisContagemSelecionados(newLocais);
      setSelectedLocalId(0);
      trackArrayModification('locaisContagemSelecionados', newLocais, originalFormData?.locaisContagemSelecionados || []);
    }
  };

  const handleRemoveLocal = () => {
    if (!selectedLocalId) return;
    const newLocais = locaisContagemSelecionados.filter((l: any) => l.id !== selectedLocalId);
    setLocaisContagemSelecionados(newLocais);
    setSelectedLocalId(0);
    trackArrayModification('locaisContagemSelecionados', newLocais, originalFormData?.locaisContagemSelecionados || []);
  };

  const handleSaveAll = async () => {
    if (id && !isNewContagem && modifiedItems.size > 0) {
      const itemSavePromises: Promise<any>[] = [];
      const errors: string[] = [];

      modifiedItems.forEach((fields, rowIndex) => {
        const item = dadosContagem[rowIndex];
        if (!item || !item.id) {
          console.warn(`Item at row ${rowIndex} has no ID, skipping save`);
          return;
        }

        const updateData: {
          quantidade_sistema?: number;
          data_validade?: string;
          quantidade_contada?: number;
          quantidade_segregada?: number;
        } = {};

        if (fields.has('sistema')) updateData.quantidade_sistema = item.sistema;

        if (fields.has('validade')) {
          // N138: este if existia desde antes da coluna virar editável, e falhava
          // EM SILÊNCIO — com a data em formato inesperado ele não montava
          // `data_validade`, o item saía do laço sem nada para enviar e a tela
          // dizia "salvo". Agora que MASTER edita de verdade, silêncio aqui vira
          // "editei a validade, salvei, e continuou a antiga". Reporta como erro.
          const partes = (item.validade || '').split('/');
          const [day, month, year] = partes;
          if (partes.length === 3 && year?.length === 4 && month && day) {
            updateData.data_validade = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          } else {
            errors.push(
              `Item ${item.produto} (Lote ${item.lote}): validade "${item.validade}" em formato inválido — não foi salva.`
            );
          }
        }

        if (fields.has('contado')) updateData.quantidade_contada = item.contado;

        if (fields.has('segregado')) updateData.quantidade_segregada = item.segregado;

        if (Object.keys(updateData).length > 0) {
          const promise = contagemService.updateItemContagem(
            parseInt(id),
            item.id,
            updateData
          ).catch(err => {
            errors.push(`Item ${item.produto} (Lote ${item.lote}): ${err.message}`);
            return null;
          });
          itemSavePromises.push(promise);
        }
      });

      if (itemSavePromises.length > 0) {
        await Promise.all(itemSavePromises);
        if (errors.length > 0) {
          msg.error(`Erros ao salvar itens:\n${errors.join('\n')}`);
          return;
        }
        msg.success(`${itemSavePromises.length} item(s) atualizado(s) com sucesso`);
      }
    }

    await saveHook.handleSave();
    setModifiedFields(new Set());
    clearModifiedItems();
    setOriginalFormData(null);
  };

  const handleDiscardChanges = () => {
    if (modifiedFields.size === 0 && modifiedItems.size === 0) {
      msg.info('Nenhuma alteração para descartar');
      setIsEditMode(false);
      return;
    }
    if (originalFormData) setFormData({ ...originalFormData });
    if (originalDadosContagem.length > 0) setDadosContagem([...originalDadosContagem]);
    setModifiedFields(new Set());
    clearModifiedItems();
    setOriginalFormData(null);
    setIsEditMode(false);
    msg.success('Alterações descartadas com sucesso');
  };

  const formatEnvioSaldos = (isoString: string | null): string => {
    if (!isoString) return '--';
    try {
      const date = new Date(isoString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch {
      return '--';
    }
  };

  return {
    locaisRetorno,
    loadingLocaisRetorno,
    handleChange,
    handleSelectHospital,
    handleAddAuditor,
    handleRemoveAuditor,
    handleAddLocal,
    handleRemoveLocal,
    handleSaveAll,
    handleDiscardChanges,
    formatEnvioSaldos,
  };
}
